import * as THREE from 'three';
import { WATER_FOG, noFog, type FogUniforms } from './skin';
import type { Sprite } from '../ui/data/spriteTypes';
import { spriteRgba } from './spriteTexture';

/**
 * Every fish in one draw (M88: four times the sprites, the user's choice,
 * "I'll batch them into a few draw calls so the page stays quick"): a
 * quad a fish, standing upright toward the camera as a sprite does, sized
 * in the scene so it grows with the zoom (the user's word: "make all objects
 * including fish grow with zoom"), its picture from one atlas of every
 * species' and phase's sprite, turned over in the shader to face right,
 * dimmed by its own opacity, and seen by the light it swims in (M96).
 */

/** Clear pixels between the atlas's pictures, so its smaller levels blend no picture into its neighbor (JUDGED: its mipmaps to the eighth). */
const PAD = 8;

/**
 * The tallest an atlas is laid, pixels: WebGL2's least `MAX_TEXTURE_SIZE` (the specification's 2048), so every device
 * takes it (M164's review: the corals' 16 pictures one above another had made 2,144).
 */
const ATLAS_MOST = 2048;

/**
 * The atlas of the fish's pictures, one above another, in columns side by side where they would stand taller than
 * `ATLAS_MOST`, each picture's place in it as UVs (u0, v0, u1, v1, the first row the top).
 */
export function fishAtlas(sprites: readonly Sprite[]): { texture: THREE.DataTexture; rects: Map<Sprite, [number, number, number, number]> } {
  const unique = [...new Set(sprites)];
  // Each picture's column and its top in it, a new column begun where the next would pass the most.
  const columns: { left: number; width: number; sprites: Sprite[] }[] = [];
  let down = 0;
  for (const sprite of unique) {
    const last = columns[columns.length - 1];
    if (!last || down + sprite.height + 2 * PAD > ATLAS_MOST) {
      columns.push({ left: last ? last.left + last.width : 0, width: 0, sprites: [] });
      down = 0;
    }
    const column = columns[columns.length - 1] as (typeof columns)[number];
    column.sprites.push(sprite);
    column.width = Math.max(column.width, sprite.width + 2 * PAD);
    down += sprite.height + 2 * PAD;
  }
  const width = columns.reduce((w, c) => w + c.width, 0);
  const height = Math.max(...columns.map((c) => c.sprites.reduce((h, s) => h + s.height + 2 * PAD, 0)));
  const rgba = new Uint8Array(width * height * 4);
  const rects = new Map<Sprite, [number, number, number, number]>();
  for (const { left, sprites: own } of columns) {
    let top = 0;
    for (const sprite of own) {
      // Each picture with its clear border bled from its edge (`spriteRgba`), so no dark rim is filtered in.
      const padded: Sprite = {
        ...sprite,
        width: sprite.width + 2 * PAD,
        height: sprite.height + 2 * PAD,
        rows: [
          ...Array.from({ length: PAD }, () => '.'.repeat(sprite.width + 2 * PAD)),
          ...sprite.rows.map((r) => `${'.'.repeat(PAD)}${r}${'.'.repeat(PAD)}`),
          ...Array.from({ length: PAD }, () => '.'.repeat(sprite.width + 2 * PAD)),
        ],
      };
      const pixels = spriteRgba(padded);
      for (let y = 0; y < padded.height; y++) rgba.set(pixels.subarray(y * padded.width * 4, (y + 1) * padded.width * 4), ((top + y) * width + left) * 4);
      rects.set(sprite, [(left + PAD) / width, (top + PAD) / height, (left + PAD + sprite.width) / width, (top + PAD + sprite.height) / height]);
      top += padded.height;
    }
  }
  const texture = new THREE.DataTexture(rgba, width, height);
  // Rows kept as drawn, the first the top, as the rects count them.
  texture.flipY = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return { texture, rects };
}

/**
 * How much of an animal is drawn by its size on the screen (M105, the redesign v2's close view "without ... oversized
 * sprites"): its length a share of the screen's width, whole up to `from`, gone from `to`, smoothly between (JUDGED:
 * an animal followed is framed at two thirds of the width, `scene.follow`'s span of 1.5 times its length, and is never
 * faded; a honu and a parrotfish swimming past the camera had spanned more than all of it; M105's review: at 0.4 to
 * 0.6 of the narrower side every whale in the Blue water's shot and a followed whale were faded out).
 */
export const NEAR_FADE = { from: 0.75, to: 1 } as const;

/**
 * How much of its width a coral colony's picture is drawn nearer the camera than its foot (M172, `FishDraw.stand`), so
 * the floor and the rock about its foot hide none of it (MEASURED then, M172: the Reef's colonies seen from its home view,
 * turned 40° either way and tilted 12° lower, 752 sightings of 397 of the 465 standing on the suite's day, none cut at
 * three quarters; at a half, its footprint's edge, 133 sightings). Since M175 the Reef's colonies stand on their
 * footprints' ground (`FishDraw.ground`), this the stand of one given none.
 */
export const SOLID_STAND = 0.75;

/** How much nearer than its footprint's nearest ground a sprite's picture is drawn (M175, `FishDraw.ground`; JUDGED: a texel of the floor's paint or so, so the ground it stands over never ties with it). */
export const GROUND_MARGIN_M = 0.03;

/** `NEAR_FADE` for a sprite `lengthPx` long on a screen `widthPx` wide: the shader's rule, for the level of detail and the taps. */
export function nearFade(lengthPx: number, widthPx: number): number {
  const t = Math.min(1, Math.max(0, (lengthPx / widthPx - NEAR_FADE.from) / (NEAR_FADE.to - NEAR_FADE.from)));
  return 1 - t * t * (3 - 2 * t);
}

const AT = new THREE.Vector3();
const WAY = new THREE.Vector3();

/** A point's depth along the way `w` a camera at `eye` looks (`FishBatch.standOf`). */
function depthAlong(eye: THREE.Vector3, w: THREE.Vector3, x: number, y: number, z: number): number {
  return (x - eye.x) * w.x + (y - eye.y) * w.y + (z - eye.z) * w.z;
}

/**
 * Whether an animal at `at`, last moving the way `way` across the floor, is seen by `camera` moving right across its
 * screen (M142, the user's report: "in the blue water scene, the whales are travelling backwards", turned by the world's
 * x where that camera looks along it, its right the world's -x): the sign of its screen x's change as it moves, as the
 * fish are turned by their own move (`FishSchools.fit`); `was` where it moves straight along the camera's line of sight.
 * Without a camera, the world's +x is the screen's right, the whole tank's front view.
 */
export function facesRight(camera: THREE.Camera | null, at: { x: number; y: number; z: number }, way: { x: number; z: number }, was: boolean): boolean {
  if (!camera) return Math.abs(way.x) > 1e-9 ? way.x > 0 : was;
  if (Math.hypot(way.x, way.z) <= 1e-9) return was;
  const p = AT.set(at.x, at.y, at.z).applyMatrix4(camera.matrixWorldInverse);
  const d = WAY.set(way.x, 0, way.z).transformDirection(camera.matrixWorldInverse);
  // The screen's x is p.x / -p.z, which grows as d.x * -p.z + p.x * d.z is above 0, the sine of the angle between its
  // way and its line of sight, seen from above in the camera's frame, times how far it is from the camera.
  const across = d.x * -p.z + p.x * d.z;
  return Math.abs(across) > 1e-9 ? across > 0 : was;
}

/**
 * The pale cream a caught fish flashes (M251, the brief's section 1, stage 4: "an extremely brief pale-white or
 * pale-cream flash"; JUDGED: cream, not white, so it reads as the fish's own scales catching the light).
 */
export const FLASH_COLOR = [1, 0.96, 0.86] as const;

const VERTEX = /* glsl */ `
attribute vec3 iPosition;
attribute vec2 iSize;
attribute vec4 iRect;
attribute float iFlip;
attribute float iAlpha;
attribute float iStand;
attribute float iFlash;
attribute float iTilt;
${WATER_FOG}varying vec2 vUv;
varying float vAlpha;
varying float vFlash;
varying vec4 vFog;
void main() {
  // The quad's corner, its foot at the fish's place, laid on the view's plane so it faces the camera upright.
  vec4 mv = modelViewMatrix * vec4(iPosition, 1.0);
  // The floor's fog (\`WATER_FOG\`), at its foot (M105).
  vFog = waterFog((modelMatrix * vec4(iPosition, 1.0)).xyz);
  #ifdef SOLID
  // A solid one never faded (M164's review: a coral colony near the camera had faded, still hiding what stood behind it).
  float near = 1.0;
  #else
  // Its length a share of the screen's width, faded as it nears the camera (NEAR_FADE, M105).
  float share = iSize.x * projectionMatrix[0][0] / (2.0 * max(-mv.z, 1e-4));
  float near = 1.0 - smoothstep(${NEAR_FADE.from.toFixed(2)}, ${NEAR_FADE.to.toFixed(2)}, share);
  #endif
  vec3 foot = mv.xyz;
  vec2 corner = vec2(position.x, position.y) * iSize;
  // Tilted nose down by iTilt radians about its middle (FishDraw.tilt, M265: a grazer bent to the reef); its nose its
  // left as drawn, its right where turned over. None for every sprite not told to.
  if (iTilt != 0.0) {
    float a = iFlip > 0.5 ? -iTilt : iTilt;
    vec2 mid = vec2(0.0, 0.5 * iSize.y);
    vec2 p = corner - mid;
    corner = mid + vec2(p.x * cos(a) - p.y * sin(a), p.x * sin(a) + p.y * cos(a));
  }
  mv.xy += corner;
  gl_Position = projectionMatrix * mv;
  #if defined(SOLID) && !defined(AT_FOOT)
  // A solid one's picture drawn its own share of its width nearer the camera than its foot (iStand, FishDraw.stand;
  // never past half the way to the camera), so the floor and the rock about its foot never cut it, whichever way it is
  // seen (M172, the user's word: "the coral looks bad because it looks unnaturally cut off. and the amount that is cut off
  // changes with viewing angle"). It writes no depth; its twin writes its depth at its foot (depthPass, AT_FOOT), so what
  // stands in front of its foot is drawn over it and what stands behind is hidden, as before (M172's review: drawn
  // forward and writing depth there, a fish in front of a colony had been hidden by it).
  float footAway = length(foot);
  float stand = min(iStand * iSize.x, 0.5 * footAway);
  vec4 standAt = projectionMatrix * vec4(foot * (1.0 - stand / max(footAway, 1e-4)), 1.0);
  gl_Position.z = standAt.z / standAt.w * gl_Position.w;
  #endif
  // The picture faces left; turned over to face right. Its first row is the atlas's top, v down.
  float u = mix(uv.x, 1.0 - uv.x, iFlip);
  vUv = vec2(mix(iRect.x, iRect.z, u), mix(iRect.w, iRect.y, uv.y));
  vAlpha = iAlpha * near;
  vFlash = iFlash;
}
`;

const FRAGMENT = /* glsl */ `
uniform sampler2D map;
uniform vec3 uLight;
varying vec2 vUv;
varying float vAlpha;
varying float vFlash;
varying vec4 vFog;
void main() {
  vec4 texel = texture2D(map, vUv);
  // A caught fish's flash (FishDraw.flash, M251): its pixels drawn toward the pale cream, its clear ones still clear.
  texel.rgb += (vec3(${FLASH_COLOR.map((c) => c.toFixed(2)).join(', ')}) - texel.rgb) * vFlash;
  #ifdef SOLID
  // A solid one's picture cut at half and drawn whole, as the depth it writes is (M164's review: its smaller levels'
  // soft edge, half seen, had written depth, a rim of the rock behind cut into a fish swimming behind it).
  if (texel.a < 0.5) discard;
  #ifdef DEPTH_FADES
  if (vAlpha < 0.5) discard;
  #endif
  gl_FragColor = vec4(mix(texel.rgb * uLight, vFog.rgb, vFog.a), vAlpha);
  #else
  // Its clear pixels cut away whatever its opacity (an alpha test at the night's 0.45 hid a sprite whole, M83).
  if (texel.a < 0.1) discard;
  gl_FragColor = vec4(mix(texel.rgb * uLight, vFog.rgb, vFog.a), texel.a * vAlpha);
  #endif
  #include <colorspace_fragment>
}
`;

/** One fish as drawn: its foot, its size, its picture, whether it faces right, and its opacity. */
export interface FishDraw {
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  rect: readonly [number, number, number, number];
  right: boolean;
  alpha: number;
  /** A solid sprite's share of its width its picture is drawn nearer the camera than its foot (M172; none unless said): the coral colonies', `SOLID_STAND`. */
  stand?: number;
  /**
   * A solid sprite's footprint's ground (M175): points (x, y, z, in threes) of the floor and the rock it stands over;
   * where given, its picture is drawn, from wherever the camera is, at the nearest of them along the way the camera
   * looks (`GROUND_MARGIN_M` nearer still), in place of `stand`: so what it stands over never hides it, and what stands
   * in front of its footprint, nearer than all of that, always does.
   */
  ground?: Float32Array;
  /** The point it is ordered by from the camera (M157), its place where none: a calf its mother's, so it stays drawn in front of her. */
  sortAt?: { x: number; y: number; z: number };
  /** How far its picture is drawn toward `FLASH_COLOR`, 0 to 1 (M251: a hunt's prey the instant it is caught); none unless said. */
  flash?: number;
  /** How far its picture is tilted nose down about its middle, radians (M265: a grazer bent to the reef); none unless said. */
  tilt?: number;
}


/** The most places a sprite may move, on the mean, for a batch to be ordered from its last order by insertion (`orderFrom`, M244; JUDGED). */
const RESORT_MOVES = 8;

export class FishBatch {
  readonly mesh: THREE.Mesh;
  private readonly geometry = new THREE.InstancedBufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private capacity = 0;
  /**
   * Its sprites as last given, and the camera's place it last drew from (in its own space), null until it has drawn:
   * written far to near from there (M157, the user's report: "creatures further away should not be seen in front of
   * those closer to you. the further whale is blocking the nearer one"). They write no depth (their clear pixels cut
   * away, their fading half seen), so one drawn later covered one nearer whatever its distance.
   */
  /** A solid batch's twin, writing its sprites' depth at their feet and no color (M172); none for the rest. */
  readonly depthPass: THREE.Mesh | null = null;
  private fish: readonly FishDraw[] = [];
  private eye: THREE.Vector3 | null = null;
  private readonly forward = new THREE.Vector3();
  /** The camera's place and way as `sortFor` reads them, before they are kept (scratch, never shared). */
  private readonly lookEye = new THREE.Vector3();
  private readonly lookForward = new THREE.Vector3();
  private readonly lookAt = new THREE.Vector3();
  private written: number[] = [];
  /** Whether `set` has written since it was last drawn: then already ordered, the draw's own ordering not needed. */
  private fresh = false;
  /** Whether its order is the one from the camera's place and way it keeps (a `set`'s is its last camera's until drawn again). */
  private sorted = false;
  /** Whether any of its sprites stands on its footprint's ground (`FishDraw.ground`), its stand then found at each draw. */
  private grounded = false;

  /**
   * @param depthTest whether it is hidden behind what stands nearer (a habitat scene's, M110: its fish swim between the corals); else drawn over the floor and the reef, the whole tank's
   * @param depthWrite whether it hides what stands behind it (the reef's coral colonies', M164: a fish behind a colony hidden by it, as by the 3D colonies before), and so is drawn solid: never faded near the camera, its picture cut at half and drawn whole
   * @param depthFades whether a solid one less than half seen, fading in or out, writes no depth (the whale over the reef's, M210's second review), so what stands behind it shows through as it fades
   */
  constructor(readonly texture: THREE.Texture, depthTest = false, depthWrite = false, depthFades = false) {
    const quad = new THREE.PlaneGeometry(1, 1);
    // Its foot at the place: the quad from -0.5 to 0.5 across and 0 to 1 up.
    quad.translate(0, 0.5, 0);
    this.geometry.index = quad.index;
    this.geometry.setAttribute('position', quad.getAttribute('position'));
    this.geometry.setAttribute('uv', quad.getAttribute('uv'));
    this.material = new THREE.ShaderMaterial({
      // No fog until it is given one (`fogUniforms`): its density none.
      uniforms: {
        map: { value: texture }, uLight: { value: new THREE.Color(1, 1, 1) }, ...noFog(),
      },
      vertexShader: VERTEX, fragmentShader: FRAGMENT, transparent: true, depthTest, depthWrite, defines: depthWrite ? { SOLID: '' } : {},
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    // A solid one's picture writes no depth, drawn toward the camera (`FishDraw.stand`); its twin writes its depth at
    // its foot and draws no color (M172), drawn just after it (`renderOrder` its owner's).
    if (depthWrite) {
      this.material.depthWrite = false;
      this.depthPass = new THREE.Mesh(this.geometry, new THREE.ShaderMaterial({
        uniforms: this.material.uniforms, vertexShader: VERTEX, fragmentShader: FRAGMENT, transparent: true, depthTest, depthWrite: true, colorWrite: false, defines: depthFades ? { SOLID: '', AT_FOOT: '', DEPTH_FADES: '' } : { SOLID: '', AT_FOOT: '' },
      }));
      this.depthPass.frustumCulled = false;
      this.depthPass.raycast = () => undefined;
    }
    // Drawn over the floor and the reef (a shore column's reef lies under its slope), as the fish's sprites were.
    this.mesh.renderOrder = 4;
    this.mesh.frustumCulled = false;
    // Each time it is drawn, the camera's place and way it looks kept, and its sprites ordered from them where no `set`
    // has since (a view held still): the renderer has sent its attributes by then, so shown the next frame, as `set`'s
    // order from the same place is.
    this.mesh.onBeforeRender = (_renderer, _scene, camera) => this.sortFor(camera);
    this.resize(64);
  }

  private resize(capacity: number): void {
    this.capacity = capacity;
    const make = (width: number): THREE.InstancedBufferAttribute => new THREE.InstancedBufferAttribute(new Float32Array(capacity * width), width).setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('iPosition', make(3));
    this.geometry.setAttribute('iSize', make(2));
    this.geometry.setAttribute('iRect', make(4));
    this.geometry.setAttribute('iFlip', make(1));
    this.geometry.setAttribute('iAlpha', make(1));
    this.geometry.setAttribute('iStand', make(1));
    this.geometry.setAttribute('iFlash', make(1));
    this.geometry.setAttribute('iTilt', make(1));
  }

  /** Draws `fish`, the batch's all, far to near from where the camera last drew it (as given, before it has drawn); grown to hold them. */
  set(fish: readonly FishDraw[]): void {
    if (fish.length > this.capacity) {
      this.geometry.dispose();
      this.resize(Math.max(fish.length, this.capacity * 2));
    }
    this.fish = fish;
    this.grounded = fish.some((f) => f.ground);
    this.write(this.orderFrom());
    this.geometry.instanceCount = fish.length;
    this.fresh = true;
  }

  /** The camera it is drawn by (M157): its place and the way it looks kept, in its own space, and its sprites ordered from them where `set` has not since. */
  sortFor(camera: THREE.Camera): void {
    const eye = this.mesh.worldToLocal(camera.getWorldPosition(this.lookEye));
    const forward = this.mesh.worldToLocal(camera.getWorldDirection(this.lookForward).add(camera.getWorldPosition(this.lookAt))).sub(eye).normalize();
    // Ordered already from this place and way, nothing set since (review M181: the Reef's scene hook and the mesh's own
    // both ordered it every frame, 0.53 ms each, MEASURED under tsx; and a camera held still sent the stands again
    // every frame).
    if (!this.fresh && this.sorted && this.eye !== null && eye.equals(this.eye) && forward.equals(this.forward)) return;
    this.eye = (this.eye ?? new THREE.Vector3()).copy(eye);
    this.forward.copy(forward);
    if (!this.fresh) {
      const order = this.orderFrom();
      if (order.length !== this.written.length || order.some((k, i) => k !== this.written[i])) this.write(order);
      else if (this.grounded) this.writeStands(order);
    } else if (this.grounded) this.writeStands(this.written);
    this.sorted = !this.fresh;
    // (Just set, its order is its last camera's, its stands written for this one: M175's test, which found them left for
    // the last camera, or at their feet before the first.)
    this.fresh = false;
  }

  /**
   * A grounded sprite's stand from where the camera is now (`FishDraw.ground`): the way along its line to its foot,
   * as a share of its width, that brings its picture to its footprint's nearest ground's depth along the way the camera
   * looks, less `GROUND_MARGIN_M`; its own `stand` where it has no ground or before it is first drawn.
   */
  private standOf(f: FishDraw): number {
    const [eye, w] = [this.eye, this.forward];
    if (!f.ground || !eye) return f.stand ?? 0;
    const foot = depthAlong(eye, w, f.x, f.y, f.z);
    if (foot <= 1e-4) return 0;
    let nearest = foot;
    for (let k = 0; k + 2 < f.ground.length; k += 3) nearest = Math.min(nearest, depthAlong(eye, w, f.ground[k] as number, f.ground[k + 1] as number, f.ground[k + 2] as number));
    const away = Math.hypot(f.x - eye.x, f.y - eye.y, f.z - eye.z);
    // Along its line to its foot, a step of 1 brings it foot / away nearer along the way the camera looks.
    return ((foot - nearest + GROUND_MARGIN_M) * (away / foot)) / Math.max(f.width, 1e-6);
  }

  /** The grounded sprites' stands written again for the camera now, their order as it was. */
  private writeStands(order: number[]): void {
    const stand = this.geometry.getAttribute('iStand') as THREE.InstancedBufferAttribute | undefined;
    if (!stand) return;
    for (let i = 0; i < order.length; i++) stand.setX(i, this.standOf(this.fish[order[i] as number] as FishDraw));
    stand.needsUpdate = true;
  }

  /**
   * Its sprites' indices far to near by their depth along the way the camera looks (each drawn flat to the screen, so
   * the nearer plane is in front wherever two overlap; M157's review: by the straight line to each, one off to the side
   * on a nearer plane counted farther), of its place (its foot, the quad's whole depth) or its own `sortAt`; equal
   * depths in their given order; as given before it is first drawn.
   */
  private orderFrom(): number[] {
    const order = this.fish.map((_f, i) => i);
    const eye = this.eye;
    if (!eye) return order;
    const f0 = this.forward;
    const depth = this.fish.map((f) => {
      const p = f.sortAt ?? f;
      return (p.x - eye.x) * f0.x + (p.y - eye.y) * f0.y + (p.z - eye.z) * f0.z;
    });
    // Farther first, equal depths by their given order: the stable sort's order, as one total order (M244).
    const before = (a: number, b: number): boolean => (depth[a] as number) > (depth[b] as number) || ((depth[a] as number) === (depth[b] as number) && a < b);
    // From the last frame's order, nearly this one's as the camera moves (review M244's: the Reef's 6,900 sprites with
    // its limu took 1.6 ms a frame to sort afresh, MEASURED under tsx), by insertion; afresh where it was set since or
    // its order has moved far (more than `RESORT_MOVES` a sprite).
    if (!this.fresh && this.written.length === order.length) {
      const next = this.written.slice();
      let moves = 0;
      for (let i = 1; i < next.length && moves <= RESORT_MOVES * next.length; i++) {
        const k = next[i] as number;
        let j = i - 1;
        while (j >= 0 && before(k, next[j] as number)) {
          next[j + 1] = next[j] as number;
          j--;
          moves++;
        }
        next[j + 1] = k;
      }
      if (moves <= RESORT_MOVES * next.length) return next;
    }
    return order.sort((a, b) => (before(a, b) ? -1 : before(b, a) ? 1 : 0));
  }

  private write(order: number[]): void {
    const a = (name: string): THREE.InstancedBufferAttribute => this.geometry.getAttribute(name) as THREE.InstancedBufferAttribute;
    const [position, size, rect, flip, alpha, stand, flash, tilt] = ['iPosition', 'iSize', 'iRect', 'iFlip', 'iAlpha', 'iStand', 'iFlash', 'iTilt'].map(a) as THREE.InstancedBufferAttribute[];
    order.forEach((k, i) => {
      const f = this.fish[k] as FishDraw;
      position?.setXYZ(i, f.x, f.y, f.z);
      size?.setXY(i, f.width, f.height);
      rect?.setXYZW(i, ...f.rect);
      flip?.setX(i, f.right ? 1 : 0);
      alpha?.setX(i, f.alpha);
      stand?.setX(i, this.standOf(f));
      flash?.setX(i, f.flash ?? 0);
      tilt?.setX(i, f.tilt ?? 0);
    });
    for (const attribute of [position, size, rect, flip, alpha, stand, flash, tilt]) if (attribute) attribute.needsUpdate = true;
    this.written = order;
  }

  /**
   * The water they are seen through (M105, the redesign v2's "distant fish slightly less contrasty"): the floor's fog's
   * uniforms, for its owner to set as it sets the floor's (`ExhibitView.setFog`), so an animal fades into the water as
   * the floor behind it does.
   */
  fogUniforms(): Partial<FogUniforms> {
    return this.material.uniforms as unknown as Partial<FogUniforms>;
  }

  /** The light the fish are seen by, each channel 0 to 1 (white, as drawn, unless set: the exhibit's hour, M96). */
  setLight(r: number, g: number, b: number): void {
    (this.material.uniforms.uLight?.value as THREE.Color).setRGB(r, g, b);
  }

  /** The light the fish are seen by, for the tests. */
  light(): [number, number, number] {
    const c = this.material.uniforms.uLight?.value as THREE.Color;
    return [c.r, c.g, c.b];
  }

  /** The fish drawn now, as the batch holds them, for the tests. */
  drawn(): FishDraw[] {
    const a = (name: string): THREE.InstancedBufferAttribute => this.geometry.getAttribute(name) as THREE.InstancedBufferAttribute;
    return Array.from({ length: this.geometry.instanceCount }, (_, i) => ({
      x: a('iPosition').getX(i), y: a('iPosition').getY(i), z: a('iPosition').getZ(i),
      width: a('iSize').getX(i), height: a('iSize').getY(i),
      rect: [a('iRect').getX(i), a('iRect').getY(i), a('iRect').getZ(i), a('iRect').getW(i)] as const,
      right: a('iFlip').getX(i) === 1,
      alpha: a('iAlpha').getX(i),
      ...(a('iStand').getX(i) > 0 ? { stand: a('iStand').getX(i) } : {}),
      ...(a('iFlash').getX(i) > 0 ? { flash: a('iFlash').getX(i) } : {}),
      ...(a('iTilt').getX(i) !== 0 ? { tilt: a('iTilt').getX(i) } : {}),
    }));
  }

  /** @param texture whether its atlas goes with it (not where the atlas is shared: the coral colonies', M164) */
  dispose(texture = true): void {
    this.geometry.dispose();
    this.material.dispose();
    (this.depthPass?.material as THREE.Material | undefined)?.dispose();
    if (texture) this.texture.dispose();
  }
}
